import { __decorate } from "tslib";
import React, { Children, isValidElement } from 'react';
import { action, computed, get, observable, runInAction, set } from 'mobx';
import sortBy from 'lodash/sortBy';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import defaultTo from 'lodash/defaultTo';
import measureScrollbar from '../../../lib/_util/measureScrollbar';
import { isCalcSize, scaleSize, toPx } from '../../../lib/_util/UnitConvertor';
import Icon from '../../../lib/icon';
import isFunction from 'lodash/isFunction';
import noop from 'lodash/noop';
import { ColumnDefaultProps, defaultAggregationRenderer } from './Column';
import CustomizationSettings from './customization-settings/CustomizationSettings';
import isFragment from '../_util/isFragment';
import ObserverCheckBox from '../check-box/CheckBox';
import ObserverRadio from '../radio/Radio';
import { DataSetSelection, FieldType, RecordCachedType } from '../data-set/enum';
import { ColumnAlign, ColumnLock, DragColumnAlign, GroupType, RowBoxPlacement, ScrollPosition, SelectionMode, TableAutoHeightType, TableBoxSizing, TableColumnTooltip, TableEditMode, TableHeightType, TableMode, TableQueryBarType, } from './enum';
import { stopPropagation } from '../_util/EventManager';
import { getCachedSelectableCounts, getCachedSelectableRecords, getColumnKey, getCurrentSelectableCounts, getHeader, isDisabledRow } from './utils';
import getReactNodeText from '../_util/getReactNodeText';
import ColumnGroups from './ColumnGroups';
import autobind from '../_util/autobind';
import { Size } from '../core/enum';
import { $l } from '../locale-context';
import CustomizationColumnHeader from './customization-settings/CustomizationColumnHeader';
import ComboCustomizationSettings from './combo-customization-settings';
import Dropdown from '../dropdown/Dropdown';
import Menu from '../menu';
import Modal from '../modal/Modal';
import { treeMap, treeSome } from '../_util/treeUtils';
import { getIf, mergeGroupStates, normalizeGroups } from '../data-set/utils';
import BatchRunner from '../_util/BatchRunner';
import { LabelLayout } from '../form/enum';
export const SELECTION_KEY = '__selection-column__'; // TODO:Symbol
export const COMBOBAR_KEY = '__combo-column__'; // TODO:Symbol
export const ROW_NUMBER_KEY = '__row-number-column__'; // TODO:Symbol
export const DRAG_KEY = '__drag-column__'; // TODO:Symbol
export const EXPAND_KEY = '__expand-column__'; // TODO:Symbol
export const CUSTOMIZED_KEY = '__customized-column__'; // TODO:Symbol
export const AGGREGATION_EXPAND_CELL_KEY = '__aggregation-expand-cell__'; // TODO:Symbol
export const BODY_EXPANDED = '__body_expanded__'; // TODO:Symbol
export const VIRTUAL_ROOT_MARGIN = 50;
function columnFilter(column) {
    return Boolean(column);
}
function getOverScanCount(tableStore, index, next) {
    const { rowMetaData } = tableStore;
    if (rowMetaData) {
        let count = 0;
        let height = 0;
        while (height < VIRTUAL_ROOT_MARGIN) {
            index += next ? 1 : -1;
            const metaData = rowMetaData[index];
            if (!metaData) {
                return count;
            }
            height += metaData.height;
            count++;
        }
        return count;
    }
    return Math.ceil(VIRTUAL_ROOT_MARGIN / tableStore.virtualRowHeight);
}
function getItemMetadata(rowMetaData, index, tableStore) {
    const { lastMeasuredIndex } = tableStore;
    if (index > lastMeasuredIndex) {
        tableStore.lastMeasuredIndex = index;
    }
    return rowMetaData[index];
}
function findNearestItemBinarySearch(rowMetaData, tableStore, high, low, offset) {
    while (low <= high) {
        const middle = low + Math.floor((high - low) / 2);
        const item = getItemMetadata(rowMetaData, middle, tableStore);
        if (!item) {
            return 0;
        }
        const currentOffset = item.offset;
        if (currentOffset === offset) {
            return middle;
        }
        if (currentOffset < offset) {
            low = middle + 1;
        }
        if (currentOffset > offset) {
            high = middle - 1;
        }
    }
    if (low > 0) {
        return low - 1;
    }
    return 0;
}
function overOffset(rowMetaData, tableStore, index, offset) {
    const item = getItemMetadata(rowMetaData, index, tableStore);
    if (item) {
        return item.offset < offset;
    }
    return false;
}
function findNearestItemExponentialSearch(rowMetaData, tableStore, index, offset) {
    const { virtualEstimatedRows } = tableStore;
    let interval = 1;
    while (index < virtualEstimatedRows && overOffset(rowMetaData, tableStore, index, offset)) {
        index += interval;
        interval *= 2;
    }
    return findNearestItemBinarySearch(rowMetaData, tableStore, Math.min(index, virtualEstimatedRows - 1), Math.floor(index / 2), offset);
}
function getVisibleStartIndex(tableStore, getLastScrollTop = () => tableStore.lastScrollTop) {
    const { height, virtualEstimatedRows } = tableStore;
    if (height === undefined || !virtualEstimatedRows) {
        return 0;
    }
    const lastScrollTop = getLastScrollTop();
    const { virtualRowHeight, rowMetaData } = tableStore;
    if (rowMetaData) {
        if (!rowMetaData.length) {
            return 0;
        }
        const { lastMeasuredIndex } = tableStore;
        const lastRowMetaData = lastMeasuredIndex > 0 ? rowMetaData[lastMeasuredIndex] : undefined;
        const lastMeasuredItemOffset = lastRowMetaData ? lastRowMetaData.offset : 0;
        if (lastMeasuredItemOffset >= lastScrollTop) {
            return findNearestItemBinarySearch(rowMetaData, tableStore, lastMeasuredIndex, 0, lastScrollTop);
        }
        return findNearestItemExponentialSearch(rowMetaData, tableStore, Math.max(0, lastMeasuredIndex), lastScrollTop);
    }
    return Math.max(0, Math.min(virtualEstimatedRows, Math.floor(lastScrollTop / virtualRowHeight)));
}
function getVisibleEndIndex(tableStore, getVirtualVisibleStartIndex = () => tableStore.virtualVisibleStartIndex, getLastScrollTop = () => tableStore.lastScrollTop) {
    const { height, virtualEstimatedRows } = tableStore;
    if (height === undefined) {
        return virtualEstimatedRows;
    }
    const virtualVisibleStartIndex = getVirtualVisibleStartIndex();
    const { virtualRowHeight, rowMetaData } = tableStore;
    if (rowMetaData) {
        if (!rowMetaData.length) {
            return 0;
        }
        const itemMetadata = getItemMetadata(rowMetaData, virtualVisibleStartIndex, tableStore);
        if (!itemMetadata) {
            return 0;
        }
        const maxOffset = getLastScrollTop() + height;
        let offset = itemMetadata.offset + itemMetadata.height;
        let stopIndex = virtualVisibleStartIndex;
        while (stopIndex < virtualEstimatedRows - 1 && offset < maxOffset) {
            stopIndex++;
            const item = getItemMetadata(rowMetaData, stopIndex, tableStore);
            if (item) {
                offset += item.height;
            }
        }
        return Math.max(0, Math.min(virtualEstimatedRows, stopIndex + 1));
    }
    const numVisibleItems = Math.ceil(height / virtualRowHeight);
    return Math.max(0, Math.min(virtualEstimatedRows, virtualVisibleStartIndex + numVisibleItems));
}
function getStartIndex(tableStore, getVirtualVisibleStartIndex = () => tableStore.virtualVisibleStartIndex) {
    const virtualVisibleStartIndex = getVirtualVisibleStartIndex();
    return Math.max(0, virtualVisibleStartIndex - getOverScanCount(tableStore, virtualVisibleStartIndex));
}
function getEndIndex(tableStore, getVirtualVisibleEndIndex = () => tableStore.virtualVisibleEndIndex) {
    const virtualVisibleEndIndex = getVirtualVisibleEndIndex();
    const { virtualEstimatedRows } = tableStore;
    return Math.min(virtualEstimatedRows, virtualVisibleEndIndex + getOverScanCount(tableStore, virtualVisibleEndIndex, true));
}
export function getIdList(store) {
    const { mouseBatchChooseStartId, mouseBatchChooseEndId, node: { element }, prefixCls } = store;
    const rows = Array.from(element.querySelectorAll(`.${prefixCls}-row`));
    let endId;
    const idList = [];
    rows.some((row) => {
        const index = Number(row.dataset.index);
        if (!endId) {
            if (mouseBatchChooseStartId === index) {
                endId = mouseBatchChooseEndId;
            }
            else if (mouseBatchChooseEndId === index) {
                endId = mouseBatchChooseStartId;
            }
        }
        if (endId) {
            idList.push(index);
        }
        return endId === index;
    });
    return idList;
}
function getRowNumbers(record, dataSet, isTree) {
    if (record && dataSet) {
        if (record.isCached) {
            return [];
        }
        const { paging, currentPage, pageSize } = dataSet;
        const pageIndex = (isTree ? paging === 'server' : paging) ? (currentPage - 1) * pageSize : 0;
        if (isTree) {
            return record.path.map((r, index) => r.indexInParent + 1 + (index === 0 ? pageIndex : 0));
        }
        return [record.index + 1 + pageIndex];
    }
    return [0];
}
function hasCheckField({ editor, name, hidden }, checkField) {
    return !hidden && !!editor && checkField === name;
}
function renderSelectionBox({ record, store }) {
    const { dataSet } = record;
    if (dataSet) {
        const { selection } = dataSet;
        const handleChange = value => {
            if (store.props.selectionMode === SelectionMode.mousedown) {
                // 将处理逻辑交给 mousedown 的处理逻辑 不然会两次触发导致不被勾选上
                return;
            }
            if (value) {
                dataSet.select(record);
            }
            else {
                dataSet.unSelect(record);
            }
        };
        const handleClick = e => {
            stopPropagation(e);
            if (selection === DataSetSelection.multiple) {
                const { lastSelected } = store;
                if (lastSelected) {
                    const nativeEvent = e.nativeEvent;
                    let startIndex = -1;
                    let endIndex = -1;
                    if (nativeEvent.shiftKey) {
                        const pointKeys = new Set([lastSelected.key, record.key]);
                        dataSet.some((pointRecord, index) => {
                            if (pointKeys.has(pointRecord.key)) {
                                if (startIndex === -1) {
                                    startIndex = index;
                                }
                                else {
                                    endIndex = index;
                                    return true;
                                }
                            }
                            return false;
                        });
                    }
                    if (endIndex !== -1 && startIndex !== endIndex) {
                        // Batch update selections
                        const rangeRecords = dataSet.slice(startIndex, endIndex + 1);
                        const changedRecords = [];
                        const selectedKeys = new Set(dataSet.selected.map(selected => selected.key));
                        if (record.isSelected) {
                            rangeRecords.forEach(rangeRecord => {
                                if (selectedKeys.has(rangeRecord.key)) {
                                    changedRecords.push(rangeRecord);
                                }
                            });
                            dataSet.batchUnSelect(changedRecords);
                        }
                        else {
                            rangeRecords.forEach(rangeRecord => {
                                if (!selectedKeys.has(rangeRecord.key)) {
                                    changedRecords.push(rangeRecord);
                                }
                            });
                            dataSet.batchSelect(changedRecords);
                        }
                    }
                }
                store.lastSelected = record;
            }
            else if (record.isSelected) {
                dataSet.unSelect(record);
            }
        };
        if (selection === DataSetSelection.multiple) {
            const batchSelectProps = {};
            const handleDragMouseUp = action(() => {
                const { mouseBatchChooseIdList } = store;
                if (store.mouseBatchChooseState) {
                    store.mouseBatchChooseState = false;
                    store.changeMouseBatchChooseIdList([]);
                    const { mouseBatchChooseStartId, mouseBatchChooseEndId } = store;
                    if (mouseBatchChooseStartId === mouseBatchChooseEndId) {
                        return;
                    }
                    const startRecord = dataSet.findRecordById(mouseBatchChooseStartId);
                    const { isSelected } = startRecord || {};
                    if (isSelected) {
                        dataSet.batchUnSelect(mouseBatchChooseIdList);
                    }
                    else {
                        dataSet.batchSelect(mouseBatchChooseIdList);
                    }
                }
                document.removeEventListener('pointerup', handleDragMouseUp);
            });
            if (store.useMouseBatchChoose) {
                batchSelectProps.onMouseDown = action(() => {
                    store.mouseBatchChooseStartId = record.id;
                    store.mouseBatchChooseEndId = record.id;
                    store.mouseBatchChooseState = true;
                    // 为什么使用 pointerup
                    // 因为需要对disabled的元素进行特殊处理
                    // 因为状态的改变依赖 mouseup 而在disabled的元素上 无法触发mouseup事件
                    // 导致状态无法进行修正
                    // 以下两种方案通过 pointer-events:none 进行处理
                    // https://stackoverflow.com/questions/322378/javascript-check-if-mouse-button-down
                    // https://stackoverflow.com/questions/62081666/the-event-of-the-document-is-not-triggered-when-it-is-on-a-disabled-element
                    // 而使用指针事件可以突破disabled的限制
                    // https://stackoverflow.com/questions/62126515/how-to-get-the-state-of-the-mouse-through-javascript/62127845#62127845
                    document.addEventListener('pointerup', handleDragMouseUp);
                });
                batchSelectProps.onMouseEnter = () => {
                    if (store.mouseBatchChooseState) {
                        store.mouseBatchChooseEndId = record.id;
                        store.changeMouseBatchChooseIdList(getIdList(store));
                    }
                };
            }
            return (React.createElement(ObserverCheckBox, Object.assign({}, batchSelectProps, { checked: record.isSelected, onChange: handleChange, onClick: handleClick, disabled: !record.selectable, "data-selection-key": SELECTION_KEY, labelLayout: LabelLayout.none, value: true })));
        }
        if (selection === DataSetSelection.single) {
            return (React.createElement(ObserverRadio, { checked: record.isSelected, onChange: handleChange, onClick: handleClick, disabled: !record.selectable, "data-selection-key": SELECTION_KEY, value: true }));
        }
    }
}
function getCustomizedColumnByKey(key, customizedColumns) {
    if (customizedColumns) {
        return customizedColumns[key];
    }
}
function getCustomizedColumn(column, customizedColumns) {
    if (customizedColumns) {
        return getCustomizedColumnByKey(getColumnKey(column).toString(), customizedColumns);
    }
}
function mergeColumnLock(column, parent, customizedColumn) {
    if (parent) {
        column.lock = parent.lock;
    }
    else if (customizedColumn && 'lock' in customizedColumn) {
        column.lock = customizedColumn.lock;
    }
}
function mergeCustomizedColumn(column, tableStore, customizedColumn, isChildrenHideDisabled) {
    if (isChildrenHideDisabled) {
        column.hideable = false;
    }
    else {
        const field = tableStore.dataSet.getField(column.name);
        if (field) {
            const dynamicProps = field.get('dynamicProps');
            const computedProps = field.get('computedProps');
            if ((dynamicProps && dynamicProps.required) || (computedProps && computedProps.required) || field.get('required')) {
                column.hideable = false;
            }
        }
    }
    if (customizedColumn) {
        if (column.hideable === false || !tableStore.columnHideable) {
            delete customizedColumn.hidden;
        }
        if (column.resizable === false || !tableStore.columnResizable) {
            delete customizedColumn.width;
        }
        if (column.titleEditable === false || !tableStore.columnTitleEditable) {
            delete customizedColumn.title;
        }
        if (!tableStore.columnDraggable) {
            delete customizedColumn.sort;
        }
        Object.assign(column, customizedColumn);
    }
}
function findAndMergeCustomizedColumn(column, tableStore, customizedColumns, isChildrenHideDisabled) {
    const customizedColumn = getCustomizedColumn(column, customizedColumns);
    mergeCustomizedColumn(column, tableStore, customizedColumn, isChildrenHideDisabled);
}
function mergeDefaultProps(tableStore, originalColumns, tableAggregation, customizedColumns, parent = null, defaultKey = [0], columnSort = {
    left: 0,
    center: 0,
    right: 0,
}) {
    const columns = [];
    const leftColumns = [];
    const rightColumns = [];
    let hasAggregationColumn = false;
    let isHideDisabled = false;
    originalColumns.forEach((column) => {
        if (isPlainObject(column)) {
            const newColumn = { ...ColumnDefaultProps, ...column };
            if (isNil(getColumnKey(newColumn))) {
                newColumn.key = `anonymous-${defaultKey[0]++}`;
            }
            const { children, aggregation } = newColumn;
            if (!hasAggregationColumn && aggregation) {
                hasAggregationColumn = true;
            }
            if (tableAggregation || !aggregation) {
                const customizedColumn = getCustomizedColumn(newColumn, customizedColumns);
                mergeColumnLock(newColumn, parent, customizedColumn);
                if (children) {
                    const [, childrenColumns, , { hasAggregationColumn: childrenHasAggregationColumn, isHideDisabled: childrenIsHideDisabled, }] = mergeDefaultProps(tableStore, children, tableAggregation, customizedColumns, newColumn, defaultKey);
                    newColumn.children = childrenColumns;
                    if (!hasAggregationColumn && childrenHasAggregationColumn) {
                        hasAggregationColumn = true;
                    }
                    if (!isHideDisabled && childrenIsHideDisabled) {
                        isHideDisabled = true;
                    }
                    mergeCustomizedColumn(newColumn, tableStore, customizedColumn, childrenIsHideDisabled);
                }
                else {
                    mergeCustomizedColumn(newColumn, tableStore, customizedColumn);
                }
                if (!isHideDisabled && newColumn.hideable === false) {
                    isHideDisabled = true;
                }
                if (parent || !newColumn.lock) {
                    if (newColumn.sort === undefined) {
                        newColumn.sort = columnSort.center;
                    }
                    columnSort.center++;
                    columns.push(newColumn);
                }
                else if (newColumn.lock === true || newColumn.lock === ColumnLock.left) {
                    if (newColumn.sort === undefined) {
                        newColumn.sort = columnSort.left;
                    }
                    columnSort.left++;
                    leftColumns.push(newColumn);
                }
                else {
                    if (newColumn.sort === undefined) {
                        newColumn.sort = columnSort.right;
                    }
                    columnSort.right++;
                    rightColumns.push(newColumn);
                }
            }
            else if (children) {
                const [leftNodes, nodes, rightNodes, { hasAggregationColumn: childrenHasAggregationColumn, isHideDisabled: childrenIsHideDisabled, }] = mergeDefaultProps(tableStore, children, tableAggregation, customizedColumns, parent, defaultKey, parent ? undefined : columnSort);
                if (!hasAggregationColumn && childrenHasAggregationColumn) {
                    hasAggregationColumn = true;
                }
                if (!isHideDisabled && childrenIsHideDisabled) {
                    newColumn.hideable = false;
                    isHideDisabled = true;
                }
                if (parent) {
                    parent.children = [...leftNodes, ...nodes, ...rightNodes];
                }
                else {
                    leftColumns.push(...leftNodes);
                    columns.push(...nodes);
                    rightColumns.push(...rightNodes);
                }
            }
        }
    }, []);
    if (parent) {
        return [[], sortBy(columns, ({ sort }) => sort), [], { hasAggregationColumn, isHideDisabled }];
    }
    return [
        sortBy(leftColumns, ({ sort }) => sort),
        sortBy(columns, ({ sort }) => sort),
        sortBy(rightColumns, ({ sort }) => sort),
        { hasAggregationColumn, isHideDisabled },
    ];
}
function normalizeColumns(tableStore, elements, tableAggregation, customizedColumns, parent = null, defaultKey = [0], columnSort = {
    left: 0,
    center: 0,
    right: 0,
}) {
    const columns = [];
    const leftColumns = [];
    const rightColumns = [];
    let hasAggregationColumn = false;
    let isHideDisabled = false;
    const normalizeColumn = (element) => {
        if (isValidElement(element)) {
            const { props, key, type } = element;
            if (isFragment(element)) {
                const { children } = props;
                if (children) {
                    Children.forEach(children, normalizeColumn);
                }
            }
            else if (type.__PRO_TABLE_COLUMN) {
                const column = {
                    ...props,
                };
                if (key) {
                    column.key = key;
                }
                else if (isNil(getColumnKey(column))) {
                    column.key = `anonymous-${defaultKey[0]++}`;
                }
                const { children, aggregation } = column;
                if (!hasAggregationColumn && aggregation) {
                    hasAggregationColumn = true;
                }
                if (tableAggregation || !aggregation) {
                    const customizedColumn = getCustomizedColumn(column, customizedColumns);
                    mergeColumnLock(column, parent, customizedColumn);
                    const [, childrenColumns, , { hasAggregationColumn: childrenHasAggregationColumn, isHideDisabled: childrenIsHideDisabled, }] = normalizeColumns(tableStore, children, tableAggregation, customizedColumns, column, defaultKey);
                    column.children = childrenColumns;
                    if (!hasAggregationColumn && childrenHasAggregationColumn) {
                        hasAggregationColumn = childrenHasAggregationColumn;
                    }
                    if (!isHideDisabled && childrenIsHideDisabled) {
                        isHideDisabled = true;
                    }
                    mergeCustomizedColumn(column, tableStore, customizedColumn, childrenIsHideDisabled);
                    if (!isHideDisabled && column.hideable === false) {
                        isHideDisabled = true;
                    }
                    if (parent || !column.lock) {
                        if (column.sort === undefined) {
                            column.sort = columnSort.center;
                        }
                        columnSort.center++;
                        columns.push(column);
                    }
                    else if (column.lock === true || column.lock === ColumnLock.left) {
                        if (column.sort === undefined) {
                            column.sort = columnSort.left;
                        }
                        columnSort.left++;
                        leftColumns.push(column);
                    }
                    else {
                        if (column.sort === undefined) {
                            column.sort = columnSort.right;
                        }
                        columnSort.right++;
                        rightColumns.push(column);
                    }
                }
                else {
                    const [leftNodes, nodes, rightNodes, { hasAggregationColumn: childrenHasAggregationColumn, isHideDisabled: childrenIsHideDisabled, }] = normalizeColumns(tableStore, children, tableAggregation, customizedColumns, parent, defaultKey, parent ? undefined : columnSort);
                    if (!hasAggregationColumn && childrenHasAggregationColumn) {
                        hasAggregationColumn = childrenHasAggregationColumn;
                    }
                    if (!isHideDisabled && childrenIsHideDisabled) {
                        column.hideable = false;
                        isHideDisabled = true;
                    }
                    if (parent) {
                        parent.children = [...leftNodes, ...nodes, ...rightNodes];
                    }
                    else {
                        leftColumns.push(...leftNodes);
                        columns.push(...nodes);
                        rightColumns.push(...rightNodes);
                    }
                }
            }
        }
    };
    Children.forEach(elements, normalizeColumn);
    if (parent) {
        return [[], sortBy(columns, ({ sort }) => sort), [], { hasAggregationColumn, isHideDisabled }];
    }
    return [
        sortBy(leftColumns, ({ sort }) => sort),
        sortBy(columns, ({ sort }) => sort),
        sortBy(rightColumns, ({ sort }) => sort),
        { hasAggregationColumn, isHideDisabled },
    ];
}
function getColumnGroupedColumns(tableStore, groups, customizedColumns) {
    const leftGroupedColumns = [];
    const groupedColumns = [];
    const rightGroupedColumns = [];
    let hasAggregation = false;
    groups.forEach((group) => {
        const { name, type, columnProps } = group;
        if (type === GroupType.column) {
            const column = {
                ...ColumnDefaultProps,
                lock: ColumnLock.left,
                ...columnProps,
                draggable: false,
                hideable: false,
                key: `__group-${name}`,
                name,
                __tableGroup: group,
            };
            column.children = columnProps && columnProps.children ? treeMap(columnProps.children, ((col, index, parentNode) => {
                const newCol = { ...col, __tableGroup: group, lock: column.lock };
                if (!getColumnKey(col)) {
                    newCol.key = parentNode ? `${parentNode.key}-${index}` : `__group-${name}-${index}`;
                }
                findAndMergeCustomizedColumn(newCol, tableStore, customizedColumns);
                return newCol;
            }), ({ sort = Infinity }, { sort: sort2 = Infinity }) => sort - sort2) : undefined;
            findAndMergeCustomizedColumn(column, tableStore, customizedColumns);
            if (!column.lock) {
                groupedColumns.push(column);
            }
            else if (column.lock === true || column.lock === ColumnLock.left) {
                leftGroupedColumns.push(column);
            }
            else {
                rightGroupedColumns.push(column);
            }
            if (columnProps && !hasAggregation && columnProps.aggregation) {
                hasAggregation = true;
            }
        }
    });
    return [leftGroupedColumns, groupedColumns, rightGroupedColumns, hasAggregation];
}
function getHeaderGroupedColumns(tableStore, groups, tableGroups, columns, dataSet, groupedColumns, customizedColumns, parentKey) {
    const generatedColumns = new Set();
    let headerUsed = false;
    groups.forEach((group) => {
        const { name, subGroups, value } = group;
        const key = parentKey ? `${parentKey}-${value}` : value;
        const subColumns = subGroups.length ? getHeaderGroupedColumns(tableStore, subGroups, tableGroups, columns, dataSet, groupedColumns, customizedColumns, key) : columns;
        const tableGroup = tableGroups.find(($tableGroup) => name === $tableGroup.name);
        if (tableGroup) {
            const { columnProps, name: groupName, hidden } = tableGroup;
            if (hidden) {
                subColumns.forEach(col => {
                    const __originalKey = getColumnKey(col);
                    const colKey = `${key}-${__originalKey}`;
                    const newCol = {
                        ...col,
                        key: colKey,
                        __tableGroup: tableGroup,
                        __group: group,
                        __groups: groups,
                        __originalKey,
                    };
                    findAndMergeCustomizedColumn(newCol, tableStore, customizedColumns);
                    newCol.lock = false;
                    generatedColumns.add(newCol);
                });
            }
            else {
                const { length } = groupedColumns;
                if (length && !headerUsed) {
                    headerUsed = true;
                    const header = getHeader({
                        ...columnProps,
                        name: groupName,
                        dataSet,
                        group,
                        groups,
                    });
                    if (header) {
                        const oldColumn = groupedColumns[length - 1];
                        const newKey = `${key}-${getColumnKey(oldColumn)}`;
                        const newColumn = {
                            ...columnProps,
                            lock: oldColumn.lock,
                            titleEditable: false,
                            draggable: false,
                            hideable: false,
                            key: newKey,
                            header,
                            children: [oldColumn],
                            __tableGroup: tableGroup,
                        };
                        const customizedColumn = getCustomizedColumnByKey(newKey, customizedColumns);
                        mergeCustomizedColumn(newColumn, tableStore, customizedColumn);
                        groupedColumns[length - 1] = newColumn;
                    }
                }
                const renderer = columnProps && columnProps.renderer || defaultAggregationRenderer;
                const column = {
                    ...columnProps,
                    titleEditable: false,
                    key,
                    headerStyle: columnProps && columnProps.style,
                    header: () => renderer({ dataSet, record: group.totalRecords[0], name: groupName, text: value, value, headerGroup: group }),
                    children: treeMap(subColumns, (col => {
                        const __originalKey = getColumnKey(col);
                        const colKey = `${key}-${__originalKey}`;
                        const newCol = { ...col, key: colKey, __originalKey };
                        if (customizedColumns) {
                            Object.assign(newCol, customizedColumns[__originalKey], customizedColumns[colKey]);
                        }
                        newCol.lock = false;
                        return newCol;
                    }), ({ sort = Infinity }, { sort: sort2 = Infinity }) => sort - sort2),
                    __tableGroup: tableGroup,
                    __group: group,
                    __groups: groups,
                };
                findAndMergeCustomizedColumn(column, tableStore, customizedColumns);
                generatedColumns.add(column);
            }
        }
        else if (subColumns.length) {
            subColumns.forEach(column => generatedColumns.add(column));
        }
    });
    return [...generatedColumns];
}
export function normalizeGroupColumns(tableStore, columns, children, aggregation, customizedColumns) {
    const { headerTableGroups, groups, dataSet } = tableStore;
    const hasHeaderGroup = headerTableGroups.length > 0;
    const generatedColumns = columns
        ? mergeDefaultProps(tableStore, columns, aggregation, customizedColumns)
        : normalizeColumns(tableStore, children, aggregation, customizedColumns);
    const [leftOriginalColumns, originalColumns, rightOriginalColumns, { hasAggregationColumn }] = generatedColumns;
    const [leftColumnGroupedColumns, columnGroupedColumns, rightColumnGroupedColumns, hasAggregationColumnGroup] = groups.length ?
        getColumnGroupedColumns(tableStore, groups, customizedColumns) :
        [[], [], [], false];
    if (hasHeaderGroup) {
        const groupedColumns = [...leftColumnGroupedColumns, ...columnGroupedColumns];
        return [
            groupedColumns,
            getHeaderGroupedColumns(tableStore, tableStore.groupedDataWithHeader, headerTableGroups, [...leftOriginalColumns, ...originalColumns, ...rightOriginalColumns], dataSet, groupedColumns, customizedColumns),
            rightColumnGroupedColumns,
            hasAggregationColumnGroup || hasAggregationColumn,
        ];
    }
    return [
        [...leftColumnGroupedColumns, ...leftOriginalColumns],
        [...columnGroupedColumns, ...originalColumns],
        [...rightOriginalColumns, ...rightColumnGroupedColumns],
        hasAggregationColumnGroup || hasAggregationColumn,
    ];
}
async function getHeaderTexts(dataSet, columns, aggregation, headers = []) {
    const column = columns.shift();
    if (column) {
        headers.push({ name: column.name, label: await getReactNodeText(getHeader({ ...column, dataSet, aggregation })) });
    }
    if (columns.length) {
        await getHeaderTexts(dataSet, columns, aggregation, headers);
    }
    return headers;
}
function autoHeightToStyle(autoHeight, parentPaddingTop = 0) {
    const { type, diff } = autoHeight;
    const height = `calc(100% - ${diff + parentPaddingTop}px)`;
    if (type === TableAutoHeightType.minHeight) {
        return {
            height,
        };
    }
    return {
        maxHeight: height,
    };
}
export default class TableStore {
    constructor(node) {
        this.editors = new Map();
        this.mouseBatchChooseStartId = 0;
        this.mouseBatchChooseEndId = 0;
        this.inBatchExpansion = false;
        this.performanceOn = false;
        this.timing = { renderStart: 0, renderEnd: 0 };
        this.lastMeasuredIndex = 0;
        this.handleSelectAllChange = action(() => {
            if (this.allChecked) {
                this.unCheckAllCurrent();
                this.unCheckAllCached();
            }
            else {
                this.checkAllCurrent();
                this.checkAllCached();
            }
        });
        this.saveCustomizedDebounce = debounce(this.saveCustomized, 1000);
        this.stopScroll = debounce(action(() => {
            this.scrolling = false;
        }), 300);
        runInAction(() => {
            this.scrollPosition = ScrollPosition.left;
            this.mouseBatchChooseIdList = [];
            this.mouseBatchChooseState = false;
            this.showCachedSelection = false;
            this.lockColumnsHeadRowsHeight = {};
            this.lockColumnsBodyRowsHeight = {};
            this.lockColumnsFootRowsHeight = {};
            this.node = node;
            this.expandedRows = [];
            this.screenHeight = typeof window === 'undefined' ? 0 : document.documentElement.clientHeight;
            this.headerHeight = 0;
            this.footerHeight = 0;
            this.lastScrollTop = 0;
            this.customizedActiveKey = ['columns'];
            this.leftOriginalColumns = [];
            this.originalColumns = [];
            this.rightOriginalColumns = [];
            this.tempCustomized = { columns: {} };
            this.customized = { columns: {} };
            this.setProps(node.props);
            if (this.customizable) {
                this.loadCustomized();
            }
            else {
                this.initColumns();
            }
        });
    }
    get styleHeight() {
        const { autoHeight, props: { style }, parentPaddingTop } = this;
        return autoHeight ? autoHeightToStyle(autoHeight, parentPaddingTop).height : style && style.height;
    }
    get styleMaxHeight() {
        const { autoHeight, props: { style }, parentPaddingTop } = this;
        return autoHeight ? autoHeightToStyle(autoHeight, parentPaddingTop).maxHeight : style && style.maxHeight;
    }
    get styleMinHeight() {
        const { style } = this.props;
        return style && toPx(style.minHeight, this.getRelationSize);
    }
    get computedHeight() {
        if (this.heightChangeable) {
            const { customized: { heightType, height, heightDiff }, tempCustomized, } = this;
            const tempHeightType = get(tempCustomized, 'heightType');
            if (tempHeightType) {
                if (tempHeightType === TableHeightType.fixed) {
                    return get(tempCustomized, 'height');
                }
                if (tempHeightType === TableHeightType.flex) {
                    return this.screenHeight - (get(tempCustomized, 'heightDiff') || 0);
                }
                return undefined;
            }
            if (heightType) {
                if (heightType === TableHeightType.fixed) {
                    return height;
                }
                if (heightType === TableHeightType.flex) {
                    return this.screenHeight - (heightDiff || 0);
                }
                return undefined;
            }
        }
        return toPx(this.styleHeight, this.getRelationSize);
    }
    get otherHeight() {
        const { footerHeight, props: { boxSizing } } = this;
        const footerTotalHeight = footerHeight && this.overflowX ? measureScrollbar() + footerHeight : footerHeight;
        const otherHeight = this.headerHeight + footerTotalHeight;
        if (boxSizing === TableBoxSizing.wrapper) {
            const { before = 0, after = 0 } = this.siblingHeihgt || {};
            return otherHeight + before + after;
        }
        return otherHeight;
    }
    get height() {
        if (!this.isBodyExpanded) {
            return undefined;
        }
        const { computedHeight } = this;
        const maxHeight = toPx(this.styleMaxHeight, this.getRelationSize);
        const minHeight = toPx(this.styleMinHeight, this.getRelationSize);
        const isComputedHeight = isNumber(computedHeight);
        if (isComputedHeight || isNumber(minHeight) || isNumber(maxHeight)) {
            const { rowHeight, otherHeight } = this;
            const rowMinHeight = (isNumber(rowHeight) ? rowHeight : 30) + otherHeight;
            const minTotalHeight = minHeight ? Math.max(rowMinHeight, minHeight) : rowMinHeight;
            const height = defaultTo(computedHeight, this.bodyHeight + otherHeight);
            const totalHeight = Math.max(minTotalHeight, maxHeight ? Math.min(maxHeight, height) : height);
            return isComputedHeight || totalHeight !== height ? totalHeight - otherHeight : undefined;
        }
    }
    get totalHeight() {
        const { height, bodyHeight, otherHeight } = this;
        return defaultTo(height, bodyHeight) + otherHeight;
    }
    get bodyHeight() {
        if (this.propVirtual) {
            const { virtualHeight } = this;
            if (virtualHeight > 0) {
                return virtualHeight;
            }
        }
        return this.calcBodyHeight || 0;
    }
    get stickyLeft() {
        return [ScrollPosition.right, ScrollPosition.middle].includes(this.scrollPosition);
    }
    get stickyRight() {
        return [ScrollPosition.left, ScrollPosition.middle].includes(this.scrollPosition);
    }
    get performanceEnabled() {
        const performanceEnabled = this.getConfig('performanceEnabled');
        return performanceEnabled && performanceEnabled.Table;
    }
    get dataSet() {
        return this.props.dataSet;
    }
    get prefixCls() {
        return this.node.prefixCls;
    }
    get customizable() {
        const { customizedCode } = this.props;
        const { props: { queryBarProps } } = this;
        const isSimpleMode = queryBarProps && queryBarProps.simpleMode;
        if (customizedCode || (this.queryBar === TableQueryBarType.comboBar && !isSimpleMode)) {
            if ('customizable' in this.props) {
                return this.props.customizable;
            }
            return this.getConfig('tableCustomizable') || this.node.context.getCustomizable('Table');
        }
        return false;
    }
    get aggregation() {
        const { aggregation } = this.customized;
        if (aggregation !== undefined) {
            return aggregation;
        }
        const { aggregation: propAggregation } = this.props;
        return propAggregation;
    }
    get aggregationExpandType() {
        return this.customized.aggregationExpandType || 'cell';
    }
    get autoHeight() {
        const { autoHeight } = this.props;
        if (autoHeight) {
            const defaultAutoHeight = {
                type: TableAutoHeightType.minHeight,
                diff: this.getConfig('tableAutoHeightDiff') || 80,
            };
            if (isObject(autoHeight)) {
                return {
                    ...defaultAutoHeight,
                    ...autoHeight,
                };
            }
            return defaultAutoHeight;
        }
        return undefined;
    }
    get heightType() {
        if (this.heightChangeable) {
            const tempHeightType = get(this.tempCustomized, 'heightType');
            if (tempHeightType !== undefined) {
                return tempHeightType;
            }
            const { heightType } = this.customized;
            if (heightType !== undefined) {
                return heightType;
            }
        }
        return this.originalHeightType;
    }
    get originalHeightType() {
        const { styleHeight } = this;
        if (styleHeight) {
            if (isString(styleHeight) && isCalcSize(styleHeight)) {
                return TableHeightType.flex;
            }
            if (isNumber(toPx(styleHeight))) {
                return TableHeightType.fixed;
            }
        }
        return TableHeightType.auto;
    }
    get virtualCell() {
        if ('virtualCell' in this.props) {
            return this.props.virtualCell;
        }
        return this.getConfig('tableVirtualCell');
    }
    get isFixedRowHeight() {
        if (!this.aggregation || !this.hasAggregationColumn) {
            return isNumber(this.rowHeight);
        }
        return false;
    }
    get propVirtual() {
        if (this.isTree && this.rowDraggable) {
            return true;
        }
        if ('virtual' in this.props) {
            return this.props.virtual;
        }
        return this.getConfig('tableVirtual');
    }
    get virtual() {
        if (this.height !== undefined) {
            return this.propVirtual;
        }
        return false;
    }
    get tableColumnResizeTransition() {
        return this.getConfig('tableColumnResizeTransition');
    }
    get virtualRowHeight() {
        const { rowHeight, cellVerticalSize = 3 } = this;
        const normalRowHeight = scaleSize(isNumber(rowHeight) ? rowHeight + cellVerticalSize : 30 + cellVerticalSize);
        return this.aggregation && this.hasAggregationColumn ? normalRowHeight * 4 : normalRowHeight;
    }
    get virtualEstimatedRows() {
        const { rowMetaData } = this;
        if (rowMetaData) {
            return rowMetaData.length;
        }
        const { actualRows } = this;
        if (actualRows !== undefined) {
            return actualRows;
        }
        return this.data.length;
    }
    get virtualHeight() {
        const { virtualRowHeight, virtualEstimatedRows } = this;
        if (!virtualEstimatedRows) {
            return 0;
        }
        const { rowMetaData } = this;
        if (rowMetaData) {
            let { lastMeasuredIndex } = this;
            let totalSizeOfMeasuredItems = 0;
            if (lastMeasuredIndex >= virtualEstimatedRows) {
                lastMeasuredIndex = virtualEstimatedRows - 1;
            }
            if (lastMeasuredIndex >= 0) {
                const itemMetadata = rowMetaData[lastMeasuredIndex];
                if (itemMetadata) {
                    totalSizeOfMeasuredItems = itemMetadata.offset + itemMetadata.height;
                }
            }
            const numUnmeasuredItems = virtualEstimatedRows - lastMeasuredIndex - 1;
            const totalSizeOfUnmeasuredItems = numUnmeasuredItems * virtualRowHeight;
            return totalSizeOfMeasuredItems + totalSizeOfUnmeasuredItems;
        }
        return Math.round(virtualEstimatedRows * virtualRowHeight);
    }
    get virtualVisibleStartIndex() {
        return getVisibleStartIndex(this);
    }
    get virtualVisibleEndIndex() {
        return getVisibleEndIndex(this);
    }
    get virtualStartIndex() {
        return getStartIndex(this);
    }
    get virtualEndIndex() {
        return getEndIndex(this);
    }
    get virtualTop() {
        const { virtualRowHeight, virtualStartIndex } = this;
        const { rowMetaData } = this;
        if (rowMetaData && rowMetaData.length) {
            return rowMetaData[virtualStartIndex].offset;
        }
        return virtualStartIndex * virtualRowHeight;
    }
    get hidden() {
        return this.styledHidden || this.props.hidden;
    }
    get alwaysShowRowBox() {
        if ('alwaysShowRowBox' in this.props) {
            return this.props.alwaysShowRowBox;
        }
        const alwaysShowRowBox = this.getConfig('tableAlwaysShowRowBox');
        if (typeof alwaysShowRowBox !== 'undefined') {
            return alwaysShowRowBox;
        }
        return false;
    }
    get keyboard() {
        if ('keyboard' in this.props) {
            return this.props.keyboard;
        }
        const keyboard = this.getConfig('tableKeyboard');
        if (typeof keyboard !== 'undefined') {
            return keyboard;
        }
        return false;
    }
    get columnResizable() {
        if (this.currentEditRecord) {
            return false;
        }
        if ('columnResizable' in this.props) {
            return this.props.columnResizable;
        }
        return this.getConfig('tableColumnResizable') !== false;
    }
    get columnHideable() {
        if ('columnHideable' in this.props) {
            return this.props.columnHideable;
        }
        return this.getConfig('tableColumnHideable') !== false;
    }
    /**
     * 表头支持编辑
     */
    get columnTitleEditable() {
        if ('columnTitleEditable' in this.props) {
            return this.props.columnTitleEditable;
        }
        return this.getConfig('tableColumnTitleEditable') === true;
    }
    get heightChangeable() {
        if ('heightChangeable' in this.props) {
            return this.props.heightChangeable;
        }
        return this.getConfig('tableHeightChangeable') === true;
    }
    get pagination() {
        if ('pagination' in this.props) {
            return this.props.pagination;
        }
        return this.getConfig('pagination');
    }
    get dragColumnAlign() {
        if ('dragColumnAlign' in this.props) {
            return this.props.dragColumnAlign;
        }
        return this.getConfig('tableDragColumnAlign');
    }
    get columnDraggable() {
        if ('columnDraggable' in this.props) {
            return this.props.columnDraggable;
        }
        if ('dragColumn' in this.props) {
            return this.props.dragColumn;
        }
        if (this.getConfig('tableColumnDraggable') === true) {
            return true;
        }
        return this.getConfig('tableDragColumn') === true;
    }
    get rowDraggable() {
        if (this.groups.length) {
            return false;
        }
        if ('rowDraggable' in this.props) {
            return this.props.rowDraggable;
        }
        if ('dragRow' in this.props) {
            return this.props.dragRow;
        }
        if (this.getConfig('tableRowDraggable') === true) {
            return true;
        }
        return this.getConfig('tableDragRow') === true;
    }
    get size() {
        const { size } = this.customized;
        if (size !== undefined) {
            return size;
        }
        return this.props.size || Size.default;
    }
    get rowHeight() {
        const { rowHeight = defaultTo(this.getConfig('tableRowHeight'), 30) } = this.props;
        const { size } = this;
        if (typeof rowHeight === 'function') {
            return rowHeight({ size });
        }
        if (isNumber(rowHeight)) {
            switch (size) {
                case Size.large:
                    return rowHeight + 2;
                case Size.small:
                    return rowHeight - 2;
                default:
            }
        }
        return rowHeight;
    }
    get headerRowHeight() {
        const { headerRowHeight = this.getConfig('tableHeaderRowHeight') } = this.props;
        if (headerRowHeight === undefined) {
            return this.rowHeight;
        }
        const { size } = this;
        if (typeof headerRowHeight === 'function') {
            return headerRowHeight({ size });
        }
        if (isNumber(headerRowHeight)) {
            switch (size) {
                case Size.large:
                    return headerRowHeight + 2;
                case Size.small:
                    return headerRowHeight - 2;
                default:
            }
        }
        return headerRowHeight;
    }
    get footerRowHeight() {
        const { footerRowHeight = this.getConfig('tableFooterRowHeight') } = this.props;
        if (footerRowHeight === undefined) {
            return this.rowHeight;
        }
        const { size } = this;
        if (typeof footerRowHeight === 'function') {
            return footerRowHeight({ size });
        }
        if (isNumber(footerRowHeight)) {
            switch (size) {
                case Size.large:
                    return footerRowHeight + 2;
                case Size.small:
                    return footerRowHeight - 2;
                default:
            }
        }
        return footerRowHeight;
    }
    get autoFootHeight() {
        if ('autoFootHeight' in this.props) {
            return this.props.autoFootHeight;
        }
        return false;
    }
    get emptyText() {
        const { renderEmpty } = this.props;
        if (renderEmpty) {
            return renderEmpty();
        }
        return this.getConfig('renderEmpty')('Table');
    }
    get highLightRow() {
        if ('highLightRow' in this.props) {
            return this.props.highLightRow;
        }
        return this.getConfig('tableHighLightRow');
    }
    get parityRow() {
        const { parityRow } = this.customized;
        if (parityRow !== undefined) {
            return parityRow;
        }
        if ('parityRow' in this.props) {
            return this.props.parityRow;
        }
        return this.getConfig('tableParityRow') === true;
    }
    get showRemovedRow() {
        if ('showRemovedRow' in this.props) {
            return this.props.showRemovedRow;
        }
        return this.getConfig('tableShowRemovedRow') === true;
    }
    get autoFocus() {
        if ('autoFocus' in this.props) {
            return this.props.autoFocus;
        }
        return this.getConfig('tableAutoFocus') !== false;
    }
    get selectedHighLightRow() {
        if ('selectedHighLightRow' in this.props) {
            return this.props.selectedHighLightRow;
        }
        return this.getConfig('tableSelectedHighLightRow') !== false;
    }
    get editorNextKeyEnterDown() {
        if ('editorNextKeyEnterDown' in this.props) {
            return this.props.editorNextKeyEnterDown;
        }
        return this.getConfig('tableEditorNextKeyEnterDown') !== false;
    }
    get border() {
        if ('border' in this.props) {
            return this.props.border;
        }
        return this.getConfig('tableBorder') !== false;
    }
    get columnEditorBorder() {
        if ('columnEditorBorder' in this.props) {
            return this.props.columnEditorBorder;
        }
        const tableColumnEditorBorder = this.getConfig('tableColumnEditorBorder');
        if (tableColumnEditorBorder !== undefined) {
            return tableColumnEditorBorder;
        }
        return this.border;
    }
    get queryBar() {
        return this.props.queryBar || this.getConfig('queryBar');
    }
    get expandIcon() {
        return this.props.expandIcon || this.getConfig('tableExpandIcon');
    }
    get tableColumnResizeTrigger() {
        return this.getConfig('tableColumnResizeTrigger');
    }
    // get pristine(): boolean | undefined {
    //   return this.props.pristine;
    // }
    get currentEditRecord() {
        return this.dataSet.find(record => record.editing === true);
    }
    set currentEditRecord(record) {
        runInAction(() => {
            const { currentEditRecord, dataSet } = this;
            if (currentEditRecord) {
                if (currentEditRecord.isNew) {
                    dataSet.remove(currentEditRecord);
                }
                else {
                    currentEditRecord.reset();
                    currentEditRecord.editing = false;
                }
            }
            if (record) {
                record.editing = true;
            }
        });
    }
    get computedRecordCachedType() {
        return this.recordCachedType || this.defaultRecordCachedType;
    }
    get isTree() {
        return this.props.mode === TableMode.tree;
    }
    get editing() {
        return this.currentEditorName !== undefined || this.currentEditRecord !== undefined;
    }
    get hasRowBox() {
        const { dataSet, selectionMode } = this.props;
        const { alwaysShowRowBox } = this;
        if (dataSet) {
            const { selection } = dataSet;
            return selection && (selectionMode === SelectionMode.rowbox || alwaysShowRowBox);
        }
        return false;
    }
    get useMouseBatchChoose() {
        const { useMouseBatchChoose } = this.props;
        if (useMouseBatchChoose !== undefined) {
            return useMouseBatchChoose;
        }
        return this.getConfig('tableUseMouseBatchChoose');
    }
    get showCachedTips() {
        const { showCachedTips } = this.props;
        if (showCachedTips !== undefined) {
            return showCachedTips;
        }
        const tableShowCachedTips = this.getConfig('tableShowCachedTips');
        if (tableShowCachedTips !== undefined) {
            return tableShowCachedTips;
        }
        return false;
    }
    get showSelectionTips() {
        const { showSelectionTips } = this.props;
        if (showSelectionTips !== undefined) {
            return showSelectionTips;
        }
        const tableShowSelectionTips = this.getConfig('tableShowSelectionTips');
        if (tableShowSelectionTips !== undefined) {
            return tableShowSelectionTips;
        }
        return false;
    }
    get overflowX() {
        const { width } = this;
        if (width !== undefined) {
            return this.columnGroups.width > width;
        }
        return false;
    }
    get overflowY() {
        const { bodyHeight, height } = this;
        return (bodyHeight !== undefined &&
            height !== undefined &&
            height < bodyHeight + (!this.hasFooter && this.overflowX ? measureScrollbar() : 0));
    }
    get hasRowGroups() {
        if (this.cachedData.length) {
            return true;
        }
        const { groups } = this;
        if (groups) {
            return groups.some(({ type }) => type === GroupType.row);
        }
        return false;
    }
    get headerTableGroups() {
        const { groups } = this;
        return groups ? groups.filter(({ type }) => type === GroupType.header) : [];
    }
    customizedColumnHeader() {
        if (this.queryBar === TableQueryBarType.comboBar) {
            return React.createElement(ComboCustomizationSettings, null);
        }
        return React.createElement(CustomizationColumnHeader, { onHeaderClick: this.openCustomizationModal });
    }
    get customizedColumn() {
        if (this.customizable && (!this.rowDraggable || this.dragColumnAlign !== DragColumnAlign.right)) {
            return {
                key: CUSTOMIZED_KEY,
                resizable: false,
                titleEditable: false,
                align: ColumnAlign.center,
                width: scaleSize(30),
                lock: ColumnLock.right,
                header: this.customizedColumnHeader,
                headerClassName: `${this.prefixCls}-customized-column`,
            };
        }
        return undefined;
    }
    get expandColumn() {
        if (this.expandIconAsCell) {
            return {
                key: EXPAND_KEY,
                resizable: false,
                titleEditable: false,
                align: ColumnAlign.center,
                width: scaleSize(50),
                lock: true,
            };
        }
        return undefined;
    }
    get rowNumberColumn() {
        const { rowNumber } = this.props;
        if (rowNumber) {
            return {
                key: ROW_NUMBER_KEY,
                resizable: true,
                titleEditable: false,
                headerClassName: `${this.prefixCls}-row-number-column`,
                renderer: this.renderRowNumber,
                tooltip: TableColumnTooltip.overflow,
                align: ColumnAlign.center,
                width: scaleSize(50),
                lock: true,
            };
        }
        return undefined;
    }
    get selectionColumn() {
        if (this.hasRowBox) {
            const { dataSet, prefixCls } = this;
            const { rowBoxPlacement } = this.props;
            const className = `${prefixCls}-selection-column`;
            let lock = ColumnLock.left;
            if (rowBoxPlacement === RowBoxPlacement.start) {
                lock = ColumnLock.left;
            }
            else if (rowBoxPlacement === RowBoxPlacement.end) {
                lock = ColumnLock.right;
            }
            else {
                lock = false;
            }
            const selectionColumn = {
                key: SELECTION_KEY,
                resizable: false,
                titleEditable: false,
                headerClassName: className,
                className,
                footerClassName: className,
                renderer: this.renderSelectionBox,
                align: ColumnAlign.center,
                width: scaleSize(50),
                lock,
            };
            if (dataSet && dataSet.selection === DataSetSelection.multiple) {
                selectionColumn.header = this.multipleSelectionRenderer;
                selectionColumn.footer = this.multipleSelectionRenderer;
            }
            return selectionColumn;
        }
        return undefined;
    }
    get comboQueryColumn() {
        if (this.queryBar === TableQueryBarType.comboBar) {
            const { prefixCls, props: { queryBarProps } } = this;
            const showInlineSearch = queryBarProps && queryBarProps.inlineSearch;
            const showInlineSearchRender = queryBarProps && queryBarProps.inlineSearchRender;
            const className = `${prefixCls}-inline-query`;
            const lock = ColumnLock.left;
            const queryColumn = {
                key: COMBOBAR_KEY,
                header: (showInlineSearch && (React.createElement(Icon, { type: "manage_search", className: className, onClick: action(() => {
                        this.comboBarStatus = !this.comboBarStatus;
                        this.node.handleHeightTypeChange();
                    }) }))),
                resizable: false,
                titleEditable: false,
                headerClassName: className,
                className,
                footerClassName: className,
                renderer: () => queryBarProps && queryBarProps.inlineSearchRender,
                align: ColumnAlign.center,
                width: scaleSize(50),
                lock,
            };
            return showInlineSearch === true || showInlineSearchRender ? queryColumn : undefined;
        }
        return undefined;
    }
    get draggableColumn() {
        const { dragColumnAlign, rowDraggable, prefixCls } = this;
        if (dragColumnAlign && rowDraggable) {
            const draggableColumn = {
                key: DRAG_KEY,
                resizable: false,
                titleEditable: false,
                className: `${prefixCls}-drag-column`,
                renderer: this.renderDragBox,
                align: ColumnAlign.center,
                width: scaleSize(50),
            };
            if (dragColumnAlign === DragColumnAlign.left) {
                draggableColumn.lock = ColumnLock.left;
            }
            if (dragColumnAlign === DragColumnAlign.right) {
                draggableColumn.lock = ColumnLock.right;
                draggableColumn.header = this.customizable && this.customizedColumnHeader;
            }
            return draggableColumn;
        }
        return undefined;
    }
    get leftColumns() {
        const { dragColumnAlign, leftOriginalColumns, expandColumn, expandIconColumnIndex, draggableColumn, rowNumberColumn, selectionColumn, comboQueryColumn } = this;
        return observable.array([
            expandIconColumnIndex ? undefined : expandColumn,
            dragColumnAlign === DragColumnAlign.left ? draggableColumn : undefined,
            rowNumberColumn,
            selectionColumn && selectionColumn.lock === ColumnLock.left ? selectionColumn : undefined,
            comboQueryColumn,
            ...leftOriginalColumns,
        ].filter(columnFilter));
    }
    get rightColumns() {
        const { dragColumnAlign, rightOriginalColumns, draggableColumn, customizedColumn, selectionColumn } = this;
        return observable.array([
            ...rightOriginalColumns,
            dragColumnAlign === DragColumnAlign.right ? draggableColumn : undefined,
            selectionColumn && selectionColumn.lock === ColumnLock.right ? selectionColumn : undefined,
            customizedColumn,
        ].filter(columnFilter));
    }
    get columns() {
        const { leftColumns, rightColumns, selectionColumn, expandColumn, expandIconColumnIndex, props: { rowBoxPlacement } } = this;
        const originalColumns = this.originalColumns.slice();
        if (isNumber(rowBoxPlacement) && selectionColumn) {
            originalColumns.splice(rowBoxPlacement, 0, selectionColumn);
        }
        const allColumns = Array.from([
            ...leftColumns,
            ...originalColumns,
            ...rightColumns,
        ]);
        if (expandIconColumnIndex && expandColumn) {
            let lock = true;
            if (expandIconColumnIndex > leftColumns.length && expandIconColumnIndex <= leftColumns.length + originalColumns.length) {
                lock = false;
            }
            else if (expandIconColumnIndex > leftColumns.length + originalColumns.length) {
                lock = ColumnLock.right;
            }
            allColumns.splice(expandIconColumnIndex, 0, { ...expandColumn, lock });
        }
        return observable.array([
            ...allColumns,
        ]);
    }
    get columnGroups() {
        return new ColumnGroups(this.columns, this);
    }
    get leftColumnGroups() {
        return new ColumnGroups(this.leftColumns, this);
    }
    get rightColumnGroups() {
        return new ColumnGroups(this.rightColumns, this);
    }
    get leafNamedColumns() {
        return this.columnGroups.allLeafs.reduce((list, { column }) => column.name ? list.concat(column) : list, []);
    }
    get hasEmptyWidthColumn() {
        return this.columnGroups.leafs.some(({ column }) => isNil(get(column, 'width')));
    }
    get getLastEmptyWidthColumn() {
        const emptyWidthColumns = this.columnGroups.leafs.filter(({ column }) => isNil(get(column, 'width')));
        return emptyWidthColumns.length ? emptyWidthColumns[emptyWidthColumns.length - 1].column : undefined;
    }
    get hasCheckFieldColumn() {
        const { checkField } = this.dataSet.props;
        if (checkField) {
            const { aggregation } = this;
            return this.columnGroups.leafs.some(({ column }) => aggregation
                ? treeSome([column], (c) => hasCheckField(c, checkField))
                : hasCheckField(column, checkField));
        }
        return false;
    }
    get hasFooter() {
        return this.columnGroups.leafs.some(({ column }) => !!column.footer && column.key !== SELECTION_KEY);
    }
    get isAnyColumnsLeftLock() {
        return this.leftColumns.length > 0;
    }
    get isAnyColumnsRightLock() {
        return this.rightColumns.length > 0;
    }
    get isAnyColumnsLock() {
        return this.isAnyColumnsLeftLock || this.isAnyColumnsRightLock;
    }
    get cachedDataInType() {
        const { dataSet, showCachedSelection, computedRecordCachedType = RecordCachedType.selected } = this;
        if (showCachedSelection) {
            switch (computedRecordCachedType) {
                case RecordCachedType.selected:
                    return dataSet.cachedSelected;
                case RecordCachedType.add:
                    return dataSet.cachedCreated;
                case RecordCachedType.update:
                    return dataSet.cachedUpdated;
                case RecordCachedType.delete:
                    return dataSet.cachedDestroyed;
                default:
            }
        }
        return [];
    }
    get cachedData() {
        const { dataSet, showCachedSelection } = this;
        if (showCachedSelection) {
            return dataSet.cachedRecords;
        }
        return [];
    }
    get currentData() {
        const { pristine, filter: recordFilter, treeFilter } = this.props;
        const { dataSet, isTree, headerFilter } = this;
        const filter = (isTree
            ? typeof treeFilter === 'function' ? treeFilter : recordFilter
            : recordFilter);
        let data = isTree ? dataSet.treeRecords : dataSet.records;
        if (typeof filter === 'function') {
            data = data.filter(filter);
        }
        if (pristine) {
            data = data.filter(record => !record.isNew);
        }
        if (headerFilter) {
            const { filter, filterText } = headerFilter;
            if (typeof filter === 'function') {
                data = data.filter(record => filter({ record, filterText }));
            }
            else {
                const field = dataSet.getField(headerFilter.fieldName);
                const type = field && field.get('type');
                const multiple = field && field.get('multiple');
                let isLookUp = false;
                if (field &&
                    (field.get('lookupCode') ||
                        isString(field.get('lookupUrl')) ||
                        (type !== FieldType.object && (field.get('lovCode') || field.getLookup() || field.get('options'))) ||
                        field.get('lovCode'))) {
                    isLookUp = true;
                }
                data = data.filter(record => {
                    let recordText;
                    if (multiple) {
                        if (isLookUp) {
                            recordText = record.get(headerFilter.fieldName).map(value => field.getText(value)).join('');
                        }
                        else {
                            recordText = record.get(headerFilter.fieldName).join('');
                        }
                    }
                    else {
                        recordText = isLookUp ? String(field.getText(record.get(headerFilter.fieldName))) : String(record.get(headerFilter.fieldName));
                    }
                    return recordText.toLocaleLowerCase().includes(String(headerFilter.filterText).toLocaleLowerCase());
                });
            }
        }
        return data;
    }
    get treeFilter() {
        const { props: { treeFilter } } = this;
        return treeFilter;
    }
    get data() {
        return [...this.cachedData, ...this.currentData];
    }
    get cachedIndeterminate() {
        const { dataSet, showCachedSelection } = this;
        if (dataSet) {
            const [cachedSelectedLength, cachedRecordsLength] = showCachedSelection ?
                getCachedSelectableCounts(dataSet, this.computedRecordCachedType, this.showCachedTips) : [0, 0];
            if (cachedSelectedLength) {
                return cachedSelectedLength !== cachedRecordsLength;
            }
        }
        return false;
    }
    get allCachedChecked() {
        const { dataSet, showCachedSelection } = this;
        if (dataSet) {
            const [cachedSelectedLength, cachedRecordsLength] = showCachedSelection ?
                getCachedSelectableCounts(dataSet, this.computedRecordCachedType, this.showCachedTips) : [0, 0];
            if (cachedSelectedLength) {
                return cachedSelectedLength === cachedRecordsLength;
            }
        }
        return false;
    }
    get currentIndeterminate() {
        const { dataSet, filter } = this.props;
        if (dataSet) {
            const [selectedLength, currentLength] = getCurrentSelectableCounts(dataSet, filter);
            if (selectedLength) {
                return selectedLength !== currentLength;
            }
        }
        return false;
    }
    get allCurrentChecked() {
        const { dataSet, filter } = this.props;
        if (dataSet) {
            const [selectedLength, currentLength] = getCurrentSelectableCounts(dataSet, filter);
            if (selectedLength) {
                return selectedLength === currentLength;
            }
        }
        return false;
    }
    get indeterminate() {
        const { showCachedSelection } = this;
        if (showCachedSelection) {
            const { dataSet } = this;
            const [cachedSelectedLength, cachedRecordsLength] = getCachedSelectableCounts(dataSet, this.computedRecordCachedType, this.showCachedTips);
            const allLength = cachedSelectedLength + dataSet.currentSelected.length;
            return !!allLength && allLength !== (cachedRecordsLength + dataSet.records.length);
        }
        return this.currentIndeterminate;
    }
    get allChecked() {
        const { showCachedSelection } = this;
        if (showCachedSelection) {
            const { dataSet } = this;
            const [cachedSelectedLength, cachedRecordsLength] = getCachedSelectableCounts(dataSet, this.computedRecordCachedType, this.showCachedTips);
            const allLength = cachedSelectedLength + dataSet.currentSelected.length;
            return !!allLength && allLength === (cachedRecordsLength + dataSet.records.length);
        }
        return this.allCurrentChecked;
    }
    get expandIconAsCell() {
        const { expandedRowRenderer, expandIconAsCell } = this.props;
        if (expandIconAsCell !== undefined) {
            return expandIconAsCell;
        }
        return !!expandedRowRenderer && !this.isTree;
    }
    get expandIconColumnIndex() {
        const { dragColumnAlign, rowDraggable, props: { expandIconColumnIndex = 0, rowNumber }, } = this;
        if ((!expandIconColumnIndex || typeof expandIconColumnIndex !== 'number') && !this.isTree) {
            return 0;
        }
        return expandIconColumnIndex + [this.hasRowBox, rowNumber, dragColumnAlign && rowDraggable, !!this.comboQueryColumn].filter(Boolean).length;
    }
    get inlineEdit() {
        return this.props.editMode === TableEditMode.inline;
    }
    checkAllCurrent() {
        const { dataSet, filter } = this.props;
        dataSet.selectAll(filter);
    }
    unCheckAllCurrent() {
        const { dataSet } = this.props;
        dataSet.unSelectAll();
    }
    checkAllCached() {
        if (this.showCachedSelection) {
            const { dataSet, filter } = this.props;
            dataSet.batchSelect(getCachedSelectableRecords(dataSet, this.computedRecordCachedType, this.showCachedTips, filter));
        }
    }
    unCheckAllCached() {
        if (this.showCachedSelection) {
            const { dataSet, filter } = this.props;
            dataSet.batchUnSelect(getCachedSelectableRecords(dataSet, this.computedRecordCachedType, this.showCachedTips, filter));
        }
    }
    isBuiltInColumn({ key }) {
        if (isString(key)) {
            return [DRAG_KEY, SELECTION_KEY, ROW_NUMBER_KEY, CUSTOMIZED_KEY, EXPAND_KEY, COMBOBAR_KEY].includes(key);
        }
    }
    getColumnTooltip(column) {
        const { tooltip } = column;
        if (tooltip) {
            return tooltip;
        }
        return this.node.context.getTooltip('table-cell');
    }
    getColumnHeaders() {
        const { leafNamedColumns, dataSet } = this;
        return getHeaderTexts(dataSet, leafNamedColumns.slice(), this.aggregation);
    }
    showEditor(name) {
        this.currentEditorName = name;
    }
    setLastScrollTop(lastScrollTop) {
        if (this.virtual) {
            this.lastScrollTop = lastScrollTop;
            this.startScroll();
        }
    }
    hideEditor() {
        this.currentEditorName = undefined;
    }
    changeMouseBatchChooseIdList(idList) {
        this.mouseBatchChooseIdList = idList;
    }
    showNextEditor(name, reserve) {
        const { dataSet } = this;
        const { currentIndex } = dataSet;
        const record = dataSet.get(reserve ? currentIndex - 1 : currentIndex + 1);
        if (record && !isDisabledRow(record)) {
            dataSet.current = record;
            this.showEditor(name);
            return true;
        }
        return false;
    }
    setProps(props) {
        this.props = props;
        this.initGroups();
        const { showCachedSelection } = props;
        if (showCachedSelection !== undefined) {
            this.showCachedSelection = showCachedSelection;
        }
    }
    updateProps(props) {
        const { customizedCode, aggregation: oldAggregation } = this.props;
        this.setProps(props);
        if (this.customizable) {
            if (customizedCode !== props.customizedCode) {
                this.loadCustomized();
                return;
            }
            const { aggregation } = props;
            if (oldAggregation !== aggregation) {
                const { customized } = this;
                if (!isNil(aggregation) && aggregation !== customized.aggregation) {
                    customized.aggregation = aggregation;
                    this.saveCustomized();
                }
            }
        }
        this.initColumns();
    }
    initGroups() {
        const { groups = [] } = this.props;
        if (groups.length) {
            const headerGroupNames = [];
            const rowGroupNames = [];
            const groupNames = [];
            const parentFields = new Map();
            const { dataSet } = this;
            const header = [];
            const row = [];
            const column = [];
            groups.forEach((group) => {
                const { type, name, parentField } = group;
                switch (type) {
                    case GroupType.header:
                        header.push(group);
                        headerGroupNames.push(name);
                        break;
                    case GroupType.row:
                        row.push(group);
                        rowGroupNames.push(name);
                        break;
                    case GroupType.none:
                        break;
                    default: {
                        column.push(group);
                        groupNames.push(name);
                    }
                }
                if (parentField) {
                    parentFields.set(name, parentField);
                }
            });
            this.groupedData = mergeGroupStates(normalizeGroups(rowGroupNames.concat(groupNames), headerGroupNames, dataSet.records, parentFields), this.groupedData);
            this.groupedDataWithHeader = mergeGroupStates(normalizeGroups(headerGroupNames.concat(rowGroupNames, groupNames), [], dataSet.records), this.groupedDataWithHeader);
            this.groups = [...header, ...row, ...column];
        }
        else {
            this.groups = [];
            this.groupedData = [];
            this.groupedDataWithHeader = [];
        }
    }
    initColumns() {
        const { customized, customizable, aggregation } = this;
        const { columns, children } = this.props;
        const customizedColumns = customizable ? customized.columns : undefined;
        const [leftOriginalColumns, originalColumns, rightOriginalColumns, hasAggregationColumn] = normalizeGroupColumns(this, columns, children, aggregation, customizedColumns);
        this.leftOriginalColumns = leftOriginalColumns;
        this.originalColumns = originalColumns;
        this.rightOriginalColumns = rightOriginalColumns;
        this.hasAggregationColumn = hasAggregationColumn;
    }
    isAggregationCellExpanded(record, key) {
        const expandedKeys = record.getState(AGGREGATION_EXPAND_CELL_KEY);
        if (expandedKeys) {
            return expandedKeys.get(key);
        }
    }
    setAggregationCellExpanded(record, key, expanded) {
        const expandedKeys = record.getState(AGGREGATION_EXPAND_CELL_KEY) || observable.map();
        expandedKeys.set(key, expanded);
        record.setState(AGGREGATION_EXPAND_CELL_KEY, expandedKeys);
    }
    get isBodyExpanded() {
        if (!this.props.bodyExpandable) {
            return true;
        }
        const { bodyExpanded } = this.props;
        if (bodyExpanded !== undefined) {
            return bodyExpanded;
        }
        const isBodyExpanded = this.dataSet.getState(BODY_EXPANDED);
        if (isBodyExpanded !== undefined) {
            return isBodyExpanded;
        }
        return defaultTo(this.props.defaultBodyExpanded, true);
    }
    setBodyExpanded(isBodyExpanded) {
        const { bodyExpanded, onBodyExpand = noop } = this.props;
        if (bodyExpanded === undefined) {
            this.dataSet.setState(BODY_EXPANDED, isBodyExpanded);
        }
        onBodyExpand(isBodyExpanded);
    }
    isGroupExpanded(group) {
        return group.isExpanded && (!group.parent || this.isGroupExpanded(group.parent));
    }
    setGroupExpanded(group, isExpanded) {
        group.isExpanded = isExpanded;
    }
    isRowExpanded(record) {
        const { isExpanded = this.expandedRows.indexOf(record.key) !== -1 } = record;
        return isExpanded && (!this.isTree || !record.parent || this.isRowExpanded(record.parent));
    }
    /**
     *
     * @param record 想修改的record
     * @param expanded 设置是否展开
     * @param disHandler 设置是否需要触发展开事件
     */
    setRowExpanded(record, expanded, disHandler) {
        record.isExpanded = expanded;
        if (!this.inBatchExpansion) {
            const index = this.expandedRows.indexOf(record.key);
            if (expanded) {
                if (index === -1) {
                    this.expandedRows.push(record.key);
                }
            }
            else if (index !== -1) {
                this.expandedRows.splice(index, 1);
            }
        }
        const { onExpand } = this.props;
        if (onExpand && !disHandler) {
            onExpand(expanded, record);
        }
        if (expanded && this.canTreeLoadData) {
            this.onTreeNodeLoad({ record });
        }
    }
    isRowPending(record) {
        return record.pending === true;
    }
    setRowPending(record, pending) {
        record.pending = pending;
    }
    isRowLoaded(record) {
        return record.childrenLoaded === true;
    }
    setRowLoaded(record, loaded) {
        record.childrenLoaded = loaded;
    }
    isRowHover(record) {
        return this.hoverRow === record;
    }
    get canTreeLoadData() {
        const { treeLoadData, treeAsync } = this.props;
        return treeAsync || !!treeLoadData;
    }
    get cellHighlightRenderer() {
        const { cellHighlightRenderer = this.getConfig('highlightRenderer') } = this.props;
        return cellHighlightRenderer;
    }
    setRowHover(record, hover) {
        this.hoverRow = hover ? record : undefined;
    }
    expandAll() {
        this.inBatchExpansion = true;
        this.expandedRows = this.dataSet.records.map(record => {
            this.setRowExpanded(record, true);
            return record.key;
        });
        this.inBatchExpansion = false;
    }
    collapseAll() {
        this.inBatchExpansion = true;
        this.dataSet.records.forEach(record => this.setRowExpanded(record, false));
        this.expandedRows = [];
        this.inBatchExpansion = false;
    }
    async onTreeNodeLoad({ record }) {
        const { dataSet, treeLoadData, treeAsync, selectionMode } = this.props;
        const promises = [];
        this.setRowPending(record, true);
        if (treeAsync && dataSet) {
            promises.push(dataSet.queryMoreChild(record, dataSet.currentPage));
        }
        if (treeLoadData) {
            promises.push(treeLoadData({ record, dataSet }));
        }
        // 由子选父
        const parentSelect = (parent) => {
            if (!parent.isSelected &&
                parent.children &&
                parent.children.length > 0 &&
                parent.children.every(child => child.isSelected)) {
                dataSet.select(parent);
                if (parent.parent) {
                    parentSelect(parent.parent);
                }
            }
        };
        try {
            await Promise.all(promises);
            if (selectionMode === SelectionMode.treebox) {
                // 由父选子
                if (record.isSelected) {
                    defaultTo(record.children, []).forEach(child => {
                        if (!child.isSelected) {
                            dataSet.select(child);
                        }
                    });
                }
                else {
                    parentSelect(record);
                }
            }
            this.setRowLoaded(record, true);
        }
        finally {
            this.setRowPending(record, false);
        }
    }
    renderSelectionBox({ record }) {
        const { selectionBoxRenderer } = this.props;
        if (selectionBoxRenderer && isFunction(selectionBoxRenderer)) {
            const element = renderSelectionBox({ record, store: this });
            return selectionBoxRenderer({ record, element });
        }
        return renderSelectionBox({ record, store: this });
    }
    renderRowNumber({ record, dataSet }) {
        const { isTree, props: { rowNumber } } = this;
        const numbers = getRowNumbers(record, dataSet, isTree);
        const number = numbers.join('-');
        if (typeof rowNumber === 'function') {
            return rowNumber({ record, dataSet, text: number, pathNumbers: numbers });
        }
        return number;
    }
    renderDragBox({ record }) {
        const { rowDragRender } = this.props;
        if (rowDragRender && isFunction(rowDragRender.renderIcon)) {
            return rowDragRender.renderIcon({ record });
        }
        return React.createElement(Icon, { type: "baseline-drag_indicator" });
    }
    findColumnGroup(indexOrKeyOrName) {
        const { allLeafs } = this.columnGroups;
        return isNumber(indexOrKeyOrName) ? allLeafs[indexOrKeyOrName] : allLeafs.find(({ key }) => String(key) === indexOrKeyOrName);
    }
    setColumnWidth(columnGroup, width, saveToCustomization = true) {
        const { column } = columnGroup;
        if (width !== column.width) {
            this.changeCustomizedColumnValue(column, {
                width,
            }, saveToCustomization);
            const { onColumnResize } = this.props;
            if (onColumnResize) {
                const index = this.columnGroups.allLeafs.indexOf(columnGroup);
                /**
                 * onColumnResize 事件回调
                 * 回调参数：
                 * @param column
                 * @param width
                 */
                onColumnResize({ column, width, index });
            }
        }
    }
    changeCustomizedColumnValue(column, value, saveToCustomization = true) {
        const { customized: { columns } } = this;
        set(column, value);
        const columnKey = getColumnKey(column).toString();
        const oldCustomized = get(columns, columnKey);
        set(columns, columnKey, {
            ...oldCustomized,
            ...value,
        });
        if (saveToCustomization) {
            this.saveCustomizedDebounce();
        }
    }
    saveCustomized(customized) {
        if (this.customizable && this.customizedLoaded) {
            const { customizedCode } = this.props;
            if (customized) {
                this.customized = customized;
            }
            if (customizedCode) {
                const tableCustomizedSave = this.getConfig('tableCustomizedSave') || this.getConfig('customizedSave');
                tableCustomizedSave(customizedCode, this.customized, 'Table');
            }
        }
    }
    openCustomizationModal(context) {
        const { customizedCode } = this.props;
        const modalProps = {
            key: 'TABLE_CUSTOMIZATION_MODAL',
            drawer: true,
            size: Size.small,
            title: $l('Table', 'customization_settings'),
            children: React.createElement(CustomizationSettings, { context: context }),
            bodyStyle: {
                overflow: 'hidden auto',
                padding: 0,
            },
        };
        if (customizedCode) {
            modalProps.okText = $l('Table', 'save_button');
        }
        Modal.open(modalProps);
    }
    async loadCustomized() {
        const { customizedCode } = this.props;
        const { props: { queryBarProps } } = this;
        const showSimpleMode = queryBarProps && queryBarProps.simpleMode;
        if ((this.customizable && customizedCode) || (this.queryBar === TableQueryBarType.comboBar && !showSimpleMode)) {
            const tableCustomizedLoad = this.getConfig('tableCustomizedLoad') || this.getConfig('customizedLoad');
            runInAction(() => {
                delete this.customizedLoaded;
                this.loading = true;
            });
            try {
                let customized;
                if (customizedCode) {
                    customized = await tableCustomizedLoad(customizedCode, 'Table');
                }
                runInAction(() => {
                    const newCustomized = { columns: {}, ...customized };
                    this.customized = newCustomized;
                    this.initColumns();
                    const { aggregation: customAggregation } = newCustomized;
                    if (customAggregation !== undefined) {
                        const { onAggregationChange, aggregation } = this.props;
                        if (onAggregationChange && customAggregation !== aggregation) {
                            onAggregationChange(customAggregation);
                        }
                    }
                });
            }
            finally {
                this.customizedLoaded = true;
                runInAction(() => {
                    this.loading = false;
                });
            }
        }
    }
    handleAllPageSelectionMenuClick({ key }) {
        const { dataSet } = this;
        const { isAllPageSelection } = dataSet;
        switch (key) {
            case 'current': {
                if (this.allChecked) {
                    this.unCheckAllCurrent();
                    this.unCheckAllCached();
                }
                else {
                    this.checkAllCurrent();
                    this.checkAllCached();
                }
                break;
            }
            case 'all':
                dataSet.setAllPageSelection(!isAllPageSelection);
                break;
            default:
        }
    }
    renderAllPageSelectionMenu() {
        const { dataSet: { isAllPageSelection }, allChecked, prefixCls } = this;
        return (React.createElement(Menu, { prefixCls: `${prefixCls}-dropdown-menu`, onClick: this.handleAllPageSelectionMenuClick },
            React.createElement(Menu.Item, { key: "current" }, $l('Table', allChecked ? 'unselect_current_page' : 'select_current_page')),
            React.createElement(Menu.Item, { key: "all" }, $l('Table', isAllPageSelection ? 'unselect_all_page' : 'select_all_page'))));
    }
    getConfig(key) {
        return this.node.getContextConfig(key);
    }
    getProPrefixCls(suffixCls, customizePrefixCls) {
        return this.node.getContextProPrefixCls(suffixCls, customizePrefixCls);
    }
    multipleSelectionRenderer() {
        const buttons = [
            React.createElement(ObserverCheckBox, { key: "selectAll", checked: this.allChecked, indeterminate: this.indeterminate, onChange: this.handleSelectAllChange, labelLayout: LabelLayout.none, value: true }),
        ];
        if (this.props.showAllPageSelectionButton) {
            buttons.push(React.createElement(Dropdown, { key: "selectAllPage", overlay: this.renderAllPageSelectionMenu },
                React.createElement(Icon, { type: "baseline-arrow_drop_down", className: `${this.prefixCls}-page-all-select` })));
        }
        return buttons;
    }
    getRelationSize(type) {
        if (type === '%') {
            return this.parentHeight;
        }
        return this.screenHeight;
    }
    startScroll() {
        this.scrolling = true;
        this.stopScroll();
    }
    batchSetRowHeight(key, callback) {
        const batchRunner = getIf(this, 'batchRunner', () => new BatchRunner());
        batchRunner.addTask(key, callback);
    }
    isRowInView(index) {
        const { propVirtual } = this;
        if (propVirtual) {
            return true;
        }
        if (this.height === undefined) {
            return index <= 10;
        }
        const { node: { tableBodyWrap } } = this;
        const scrollTop = tableBodyWrap ? tableBodyWrap.scrollTop : 0;
        const visibleStartIndex = getVisibleStartIndex(this, () => scrollTop);
        return index >= getStartIndex(this, () => visibleStartIndex) &&
            index <= getEndIndex(this, () => getVisibleEndIndex(this, () => visibleStartIndex, () => scrollTop));
    }
    alignEditor() {
        const { currentEditorName } = this;
        if (currentEditorName) {
            const currentEditor = this.editors.get(currentEditorName);
            if (currentEditor) {
                currentEditor.alignEditor();
            }
        }
    }
}
__decorate([
    observable
], TableStore.prototype, "props", void 0);
__decorate([
    observable
], TableStore.prototype, "customized", void 0);
__decorate([
    observable
], TableStore.prototype, "tempCustomized", void 0);
__decorate([
    observable
], TableStore.prototype, "loading", void 0);
__decorate([
    observable
], TableStore.prototype, "leftOriginalColumns", void 0);
__decorate([
    observable
], TableStore.prototype, "originalColumns", void 0);
__decorate([
    observable
], TableStore.prototype, "rightOriginalColumns", void 0);
__decorate([
    observable
], TableStore.prototype, "hasAggregationColumn", void 0);
__decorate([
    observable
], TableStore.prototype, "calcBodyHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "width", void 0);
__decorate([
    observable
], TableStore.prototype, "lastScrollTop", void 0);
__decorate([
    observable
], TableStore.prototype, "lockColumnsBodyRowsHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "lockColumnsFootRowsHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "lockColumnsHeadRowsHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "expandedRows", void 0);
__decorate([
    observable
], TableStore.prototype, "hoverRow", void 0);
__decorate([
    observable
], TableStore.prototype, "rowClicked", void 0);
__decorate([
    observable
], TableStore.prototype, "currentEditorName", void 0);
__decorate([
    observable
], TableStore.prototype, "styledHidden", void 0);
__decorate([
    observable
], TableStore.prototype, "customizedActiveKey", void 0);
__decorate([
    observable
], TableStore.prototype, "mouseBatchChooseState", void 0);
__decorate([
    observable
], TableStore.prototype, "mouseBatchChooseIdList", void 0);
__decorate([
    observable
], TableStore.prototype, "columnResizing", void 0);
__decorate([
    observable
], TableStore.prototype, "scrollPosition", void 0);
__decorate([
    observable
], TableStore.prototype, "comboBarStatus", void 0);
__decorate([
    observable
], TableStore.prototype, "siblingHeihgt", void 0);
__decorate([
    observable
], TableStore.prototype, "parentHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "parentPaddingTop", void 0);
__decorate([
    observable
], TableStore.prototype, "screenHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "headerHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "footerHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "headerFilter", void 0);
__decorate([
    computed
], TableStore.prototype, "computedHeight", null);
__decorate([
    computed
], TableStore.prototype, "height", null);
__decorate([
    computed
], TableStore.prototype, "autoHeight", null);
__decorate([
    observable
], TableStore.prototype, "actualRows", void 0);
__decorate([
    observable
], TableStore.prototype, "rowMetaData", void 0);
__decorate([
    observable
], TableStore.prototype, "scrolling", void 0);
__decorate([
    observable
], TableStore.prototype, "cellVerticalSize", void 0);
__decorate([
    computed
], TableStore.prototype, "virtualRowHeight", null);
__decorate([
    computed
], TableStore.prototype, "virtualVisibleStartIndex", null);
__decorate([
    computed
], TableStore.prototype, "virtualVisibleEndIndex", null);
__decorate([
    computed
], TableStore.prototype, "currentEditRecord", null);
__decorate([
    observable
], TableStore.prototype, "showCachedSelection", void 0);
__decorate([
    observable
], TableStore.prototype, "defaultRecordCachedType", void 0);
__decorate([
    observable
], TableStore.prototype, "recordCachedType", void 0);
__decorate([
    computed
], TableStore.prototype, "overflowX", null);
__decorate([
    computed
], TableStore.prototype, "overflowY", null);
__decorate([
    computed
], TableStore.prototype, "hasRowGroups", null);
__decorate([
    computed
], TableStore.prototype, "headerTableGroups", null);
__decorate([
    autobind
], TableStore.prototype, "customizedColumnHeader", null);
__decorate([
    computed
], TableStore.prototype, "customizedColumn", null);
__decorate([
    computed
], TableStore.prototype, "expandColumn", null);
__decorate([
    computed
], TableStore.prototype, "rowNumberColumn", null);
__decorate([
    computed
], TableStore.prototype, "selectionColumn", null);
__decorate([
    computed
], TableStore.prototype, "comboQueryColumn", null);
__decorate([
    computed
], TableStore.prototype, "draggableColumn", null);
__decorate([
    computed
], TableStore.prototype, "leftColumns", null);
__decorate([
    computed
], TableStore.prototype, "rightColumns", null);
__decorate([
    computed
], TableStore.prototype, "columns", null);
__decorate([
    computed
], TableStore.prototype, "columnGroups", null);
__decorate([
    computed
], TableStore.prototype, "leftColumnGroups", null);
__decorate([
    computed
], TableStore.prototype, "rightColumnGroups", null);
__decorate([
    computed
], TableStore.prototype, "leafNamedColumns", null);
__decorate([
    computed
], TableStore.prototype, "hasEmptyWidthColumn", null);
__decorate([
    computed
], TableStore.prototype, "getLastEmptyWidthColumn", null);
__decorate([
    computed
], TableStore.prototype, "hasCheckFieldColumn", null);
__decorate([
    observable
], TableStore.prototype, "groups", void 0);
__decorate([
    observable
], TableStore.prototype, "groupedData", void 0);
__decorate([
    observable
], TableStore.prototype, "groupedDataWithHeader", void 0);
__decorate([
    computed
], TableStore.prototype, "currentData", null);
__decorate([
    computed
], TableStore.prototype, "data", null);
__decorate([
    computed
], TableStore.prototype, "cachedIndeterminate", null);
__decorate([
    computed
], TableStore.prototype, "allCachedChecked", null);
__decorate([
    computed
], TableStore.prototype, "currentIndeterminate", null);
__decorate([
    computed
], TableStore.prototype, "allCurrentChecked", null);
__decorate([
    computed
], TableStore.prototype, "indeterminate", null);
__decorate([
    computed
], TableStore.prototype, "allChecked", null);
__decorate([
    action
], TableStore.prototype, "showEditor", null);
__decorate([
    action
], TableStore.prototype, "setLastScrollTop", null);
__decorate([
    action
], TableStore.prototype, "hideEditor", null);
__decorate([
    action
], TableStore.prototype, "changeMouseBatchChooseIdList", null);
__decorate([
    action
], TableStore.prototype, "setProps", null);
__decorate([
    action
], TableStore.prototype, "updateProps", null);
__decorate([
    action
], TableStore.prototype, "initGroups", null);
__decorate([
    action
], TableStore.prototype, "initColumns", null);
__decorate([
    action
], TableStore.prototype, "setAggregationCellExpanded", null);
__decorate([
    action
], TableStore.prototype, "setRowExpanded", null);
__decorate([
    action
], TableStore.prototype, "setRowPending", null);
__decorate([
    action
], TableStore.prototype, "setRowLoaded", null);
__decorate([
    computed
], TableStore.prototype, "cellHighlightRenderer", null);
__decorate([
    action
], TableStore.prototype, "setRowHover", null);
__decorate([
    action
], TableStore.prototype, "expandAll", null);
__decorate([
    action
], TableStore.prototype, "collapseAll", null);
__decorate([
    autobind
], TableStore.prototype, "onTreeNodeLoad", null);
__decorate([
    autobind
], TableStore.prototype, "renderSelectionBox", null);
__decorate([
    autobind
], TableStore.prototype, "renderRowNumber", null);
__decorate([
    autobind
], TableStore.prototype, "renderDragBox", null);
__decorate([
    action
], TableStore.prototype, "changeCustomizedColumnValue", null);
__decorate([
    action
], TableStore.prototype, "saveCustomized", null);
__decorate([
    autobind,
    action
], TableStore.prototype, "openCustomizationModal", null);
__decorate([
    autobind,
    action
], TableStore.prototype, "handleAllPageSelectionMenuClick", null);
__decorate([
    autobind
], TableStore.prototype, "renderAllPageSelectionMenu", null);
__decorate([
    autobind
], TableStore.prototype, "getConfig", null);
__decorate([
    autobind
], TableStore.prototype, "getProPrefixCls", null);
__decorate([
    autobind
], TableStore.prototype, "multipleSelectionRenderer", null);
__decorate([
    autobind
], TableStore.prototype, "getRelationSize", null);
__decorate([
    action
], TableStore.prototype, "startScroll", null);
